import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';

/**
 * GOV-7 E-5 — Tenant360 read-only Fleet governance card.
 *
 * SuperAdmin gets governance VISIBILITY, not tenant operational control: safe COUNTS + governed-source LABELS +
 * the assigned profile/version, straight from the SuperAdmin-gated aggregate
 * (GET /superadmin/industry-profiles/tenant/{ref}/fleet-summary). It renders NO vehicle/tracker/source ids, NO
 * credentials, NO coordinates, NO ping history — and offers NO fleet CRUD (create/edit/deactivate/register/bind/
 * rotate/revoke stay tenant Business Admin/Manager operations). Missing data renders a truthful empty state.
 */
const Tenant360FleetSummary = ({ api, tenantRef }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!tenantRef) return;
    setErr(null);
    try {
      const r = await api.get(`/api/v1/superadmin/industry-profiles/tenant/${encodeURIComponent(tenantRef)}/fleet-summary`);
      setData(r.data && r.data.tenant_found !== false ? r.data : null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Fleet summary unavailable');
    } finally { setLoaded(true); }
  }, [api, tenantRef]);

  useEffect(() => { load(); }, [load]);

  const n = (v) => (typeof v === 'number' ? v : 0);
  // GOV-7 Issue A — governed allowance label (read-only): null => not configured, -1 => Unlimited, else number.
  const allowed = (v) => (v == null ? 'not configured' : (v === -1 ? 'Unlimited' : v));
  const sources = Array.isArray(data?.allowed_location_sources) ? data.allowed_location_sources : [];

  return (
    <Card data-testid="t360-fleet-summary">
      <CardHeader>
        <CardTitle>Fleet (read-only)</CardTitle>
        <CardDescription>Governance visibility only — safe counts and governed location sources. SuperAdmin has no fleet create/edit/tracker authority; those are tenant Business Admin/Manager operations.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {!loaded && <p className="text-gray-600" data-testid="t360fleet-loading">Loading…</p>}
        {loaded && err && <p className="text-red-600 text-xs" data-testid="t360fleet-error">{err}</p>}
        {loaded && !err && !data && <p className="text-gray-600" data-testid="t360fleet-empty">No fleet data for this tenant.</p>}
        {loaded && !err && data && (
          <>
            <div>
              <div className="text-gray-500 text-xs uppercase">Vehicles</div>
              <div data-testid="t360fleet-vehicles">
                Total: <b>{n(data.vehicle_count)}</b> · Active: <b>{n(data.active_vehicle_count)}</b> · Inactive: <b>{n(data.inactive_vehicle_count)}</b>
              </div>
              <div data-testid="t360fleet-vehicle-capacity" className="text-xs text-gray-600">
                <b>{n(data.active_vehicle_count)}</b> active / <b>{allowed(data.active_vehicle_limit)}</b> allowed
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase">Trackers</div>
              <div data-testid="t360fleet-trackers">
                Total: <b>{n(data.tracker_count)}</b> · Active: <b>{n(data.active_tracker_count)}</b> · Revoked: <b>{n(data.revoked_tracker_count)}</b>
              </div>
              <div data-testid="t360fleet-tracker-capacity" className="text-xs text-gray-600">
                <b>{n(data.active_tracker_count)}</b> active / <b>{allowed(data.active_tracker_limit)}</b> allowed
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase">Allowed location sources</div>
              <div data-testid="t360fleet-sources"><b>{sources.length ? sources.join(', ') : '—'}</b></div>
            </div>
            <div className="text-xs text-gray-600" data-testid="t360fleet-profile">
              Assigned profile: <b>{data.industry_profile_key || '— (none pinned)'}</b>{data.industry_profile_version ? ` v${data.industry_profile_version}` : ''}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default Tenant360FleetSummary;
